import { useEffect, useState } from "react";

import {
  AccordionProps,
  AccordionSummaryProps,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";

import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../store/Store";
import {
  FetchDataSuccessAction,
  FetchDataFailureAction,
  fetchData,
  postData,
  putData,
  deleteData,
} from "../../store/actions";
import { setInPageLoading } from "../../store/LoadingSlice";
import { ToastVariant, useCustomToast } from "../../components/Methods/SnackBar";
import Loading from "../../components/Loading/Loading";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";

import {
  skillSections,
  skillSectionDetail,
  sectionSkillMappings,
  sectionSkillMappingDelete,
  getAllSkills,
} from "../../Service/Apis";

// ── Types ──────────────────────────────────────────────────────────────────

interface SkillOption {
  skill_id: string;
  skill_name: string;
}

interface MappedSkill {
  skill_id: string;
  skill_name: string;
}

interface SectionMapping {
  id: string;
  section: string;
  skill: string;
  skill_name: string;
  section_name: string;
}

interface SkillSection {
  section_id: string;
  section_name: string;
  created_at: string;
  skills: MappedSkill[];
}

// ── Styled Accordion (matches SkillsView.tsx exactly) ─────────────────────

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": { borderBottom: 0 },
  "&:before": { display: "none" },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.00)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": { marginLeft: theme.spacing(1) },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0,0,0,.125)",
}));

// ── Component ──────────────────────────────────────────────────────────────

const SkillSectionManagement = () => {
  const [sections, setSections] = useState<SkillSection[]>([]);
  const [allSkills, setAllSkills] = useState<SkillOption[]>([]);

  // Add section dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  // Edit section name inline
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editSectionName, setEditSectionName] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // Delete section dialog
  const [deleteSectionId, setDeleteSectionId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Add skill to section
  const [addSkillSectionId, setAddSkillSectionId] = useState<string | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<SkillOption[]>([]);
  const [skillAddLoading, setSkillAddLoading] = useState(false);

  // Delete skill mapping dialog
  const [deleteMapping, setDeleteMapping] = useState<{ mappingId: string; skillName: string } | null>(null);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> =
    useDispatch();
  const isLoading = useSelector((state: any) => state.loading.inPageLoading);

  const toast = (msg: string, variant: ToastVariant) => showToast(msg, variant, true);

  // ── Fetch all sections and all skills on mount ────────────────────────

  const fetchSections = async () => {
    try {
      dispatch(setInPageLoading(true));
      const res: any = await dispatch2(fetchData(skillSections));
      setSections(res as SkillSection[]);
    } catch {
      toast("Failed to load sections.", "error");
    } finally {
      dispatch(setInPageLoading(false));
    }
  };

  const fetchAllSkills = async () => {
    try {
      const res: any = await dispatch2(fetchData(getAllSkills));
      setAllSkills(res as SkillOption[]);
    } catch {
      /* silently fail */
    }
  };

  useEffect(() => {
    fetchSections();
    fetchAllSkills();
  }, []);

  // ── Add Section ───────────────────────────────────────────────────────

  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;
    setAddLoading(true);
    try {
      await dispatch2(postData(skillSections, { section_name: newSectionName.trim() }));
      toast(`Section "${newSectionName.trim()}" created.`, "success");
      setAddDialogOpen(false);
      setNewSectionName("");
      fetchSections();
    } catch {
      toast("Failed to create section.", "error");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit Section Name ─────────────────────────────────────────────────

  const handleSaveEditSection = async (sectionId: string) => {
    if (!editSectionName.trim()) return;
    setEditLoading(true);
    try {
      await dispatch2(putData(skillSectionDetail(sectionId), { section_name: editSectionName.trim() }));
      toast("Section name updated.", "success");
      setEditingSectionId(null);
      fetchSections();
    } catch {
      toast("Failed to update section name.", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete Section ────────────────────────────────────────────────────

  const handleDeleteSection = async () => {
    if (!deleteSectionId) return;
    setDeleteLoading(true);
    try {
      await dispatch2(deleteData(skillSectionDetail(deleteSectionId)));
      toast("Section deleted.", "success");
      setDeleteSectionId(null);
      fetchSections();
    } catch {
      toast("Failed to delete section.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Add Skill to Section ──────────────────────────────────────────────

  const handleAddSkillToSection = async (sectionId: string) => {
    if (selectedSkills.length === 0) return;
    setSkillAddLoading(true);
    try {
      await Promise.all(
        selectedSkills.map((skill) =>
          dispatch2(postData(sectionSkillMappings, { section: sectionId, skill: skill.skill_id }))
        )
      );
      toast(`${selectedSkills.length} skill(s) added to section.`, "success");
      setAddSkillSectionId(null);
      setSelectedSkills([]);
      fetchSections();
    } catch {
      toast("Failed to add one or more skills. They may already be mapped to this section.", "error");
    } finally {
      setSkillAddLoading(false);
    }
  };

  // ── Remove Skill Mapping ──────────────────────────────────────────────

  const fetchMappingIdAndDelete = async () => {
    if (!deleteMapping) return;
    setDeleteLoading(true);
    try {
      await dispatch2(deleteData(sectionSkillMappingDelete(deleteMapping.mappingId)));
      toast(`"${deleteMapping.skillName}" removed from section.`, "success");
      setDeleteMapping(null);
      fetchSections();
    } catch {
      toast("Failed to remove skill from section.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Fetch mapping id before deletion (we need mapping id, not skill id)
  const handleRemoveSkill = async (sectionId: string, skillId: string, skillName: string) => {
    try {
      const res: any = await dispatch2(
        fetchData(`${sectionSkillMappings}?section_id=${sectionId}&skill_id=${skillId}`)
      );
      if (res && res.length > 0) {
        setDeleteMapping({ mappingId: res[0].id, skillName });
      }
    } catch {
      toast("Failed to find skill mapping.", "error");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (isLoading) return <Loading isLoading={isLoading} />;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header row */}
      <Box sx={{ display: "flex", justifyContent: "right", m: 1, p: 0 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Section
        </Button>
      </Box>

      {/* Section List */}
      <Box sx={{ display: "flex", justifyContent: "center", m: 2, p: 2 }}>
        <Box sx={{ width: "100%" }}>
          {sections.length === 0 ? (
            <Box sx={{ mt: 9, display: "flex", justifyContent: "center" }}>
              <NothingToDisplayComponent />
            </Box>
          ) : (
            sections.map((section, index) => (
              <Box sx={{ width: "100%", m: 1, p: 1 }} key={section.section_id}>
                <Accordion>
                  <AccordionSummary>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      {/* Section name / inline edit */}
                      {editingSectionId === section.section_id ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <TextField
                            size="small"
                            variant="standard"
                            value={editSectionName}
                            onChange={(e) => setEditSectionName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveEditSection(section.section_id);
                              }}
                              disabled={editLoading}
                            >
                              {editLoading ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSectionId(null);
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="h6" fontWeight="bold">
                          {section.section_name}
                        </Typography>
                      )}

                      {/* Action buttons */}
                      {editingSectionId !== section.section_id && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Edit section name">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSectionId(section.section_id);
                                setEditSectionName(section.section_name);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete section">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteSectionId(section.section_id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {/* Mapped skills */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                      {section.skills.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No skills mapped yet.
                        </Typography>
                      ) : (
                        section.skills.map((skill) => (
                          <Chip
                            key={skill.skill_id}
                            label={skill.skill_name}
                            onDelete={() =>
                              handleRemoveSkill(section.section_id, skill.skill_id, skill.skill_name)
                            }
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        ))
                      )}
                    </Box>

                    {/* Add skill to this section */}
                    {addSkillSectionId === section.section_id ? (
                      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mt: 1, flexWrap: "wrap" }}>
                        <Autocomplete
                          multiple
                          size="small"
                          sx={{ minWidth: 320, flex: 1 }}
                          options={allSkills.filter(
                            (s) => !section.skills.some((ms) => ms.skill_id === s.skill_id)
                          )}
                          getOptionLabel={(option) => option.skill_name}
                          value={selectedSkills}
                          onChange={(_, value) => setSelectedSkills(value as SkillOption[])}
                          disableCloseOnSelect
                          renderInput={(params) => (
                            <TextField {...params} label="Select Skills" variant="standard" />
                          )}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          disabled={selectedSkills.length === 0 || skillAddLoading}
                          onClick={() => handleAddSkillToSection(section.section_id)}
                          startIcon={skillAddLoading ? <CircularProgress size={14} /> : <AddIcon />}
                        >
                          Add
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setAddSkillSectionId(null);
                            setSelectedSkills([]);
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setAddSkillSectionId(section.section_id);
                          setSelectedSkills([]);
                        }}
                      >
                        Add Skill
                      </Button>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* ── Add Section Dialog ── */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight="bold">Add Skill Section</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant="standard"
            label="Section Name"
            placeholder="e.g. Technical, Communication"
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddSection()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); setNewSectionName(""); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newSectionName.trim() || addLoading}
            onClick={handleAddSection}
            startIcon={addLoading ? <CircularProgress size={16} /> : null}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Section Dialog ── */}
      <Dialog open={!!deleteSectionId} onClose={() => setDeleteSectionId(null)}>
        <DialogTitle fontWeight="bold">Delete Section</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this section? All skill mappings and designation
            assignments for this section will also be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="error" disabled={deleteLoading} onClick={handleDeleteSection}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}>
            Delete
          </Button>
          <Button onClick={() => setDeleteSectionId(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Skill Mapping Dialog ── */}
      <Dialog open={!!deleteMapping} onClose={() => setDeleteMapping(null)}>
        <DialogTitle fontWeight="bold">Remove Skill</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteMapping?.skillName}</strong> from this section?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="error" disabled={deleteLoading} onClick={fetchMappingIdAndDelete}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}>
            Remove
          </Button>
          <Button onClick={() => setDeleteMapping(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SkillSectionManagement;
